import { Drawer as BaseDrawer } from '@base-ui/react/drawer';
import { FC } from 'react';

import ArrowLeftIcon from '@/assets/svg/ui-elements/arrow-back.svg';
import CloseIcon from '@/assets/svg/ui-elements/close.svg';

import Button, { IconButton } from '../Button';
import { HeaderIcon } from '../Modal/Modal.styled';
import Typography from '../Typography';
import {
  ActionRow,
  Actions,
  CloseButton,
  DragIndicator,
  Handle,
  Header,
  SheetContent,
  StyledBackdrop,
  StyledContent,
  StyledPopup,
  StyledViewport,
} from './BottomSheet.styled';
import { BottomSheetProps } from './types';

/**
 * A sheet that rises from the bottom edge and is dismissed by swiping down.
 *
 * The mobile counterpart to `Drawer`: same Base UI primitive and the same prop shape, anchored
 * to a different edge. Reach for this when a phone-width layout would otherwise need a fourth
 * navigation step — a sheet keeps the thing underneath on screen, so filters, pickers and
 * detail panes stay layered over the list instead of replacing it.
 */
const BottomSheet: FC<BottomSheetProps> = ({
  open,
  onOpenChange,
  modal,

  children,

  title,
  headerIcon,
  onBack,
  showClose = true,
  maxHeight,
  hideHandle = false,

  primaryAction,
  secondaryAction,
  iconAction,
}) => {
  const backButton = onBack && (
    <IconButton
      type="button"
      color="tertiary"
      variant="ghost"
      size="xs"
      aria-label="Back"
      onClick={onBack}
    >
      <ArrowLeftIcon />
    </IconButton>
  );

  const closeButton = showClose && (
    <BaseDrawer.Close
      render={
        <CloseButton type="button" color="tertiary" variant="ghost" size="xs" aria-label="Close">
          <CloseIcon />
        </CloseButton>
      }
    />
  );

  const primaryButton = primaryAction && (
    <Button
      color={primaryAction.color ?? 'primary'}
      variant={primaryAction.variant ?? 'solid'}
      size="md"
      fullWidth
      onClick={primaryAction.onClick}
      disabled={primaryAction.disabled}
    >
      {primaryAction.children}
    </Button>
  );

  const secondaryButton = secondaryAction && (
    <Button
      color={secondaryAction.color ?? 'tertiary'}
      variant={secondaryAction.variant ?? 'solid'}
      size="md"
      fullWidth
      onClick={secondaryAction.onClick}
      disabled={secondaryAction.disabled}
    >
      {secondaryAction.children}
    </Button>
  );

  const iconButton = iconAction && (
    <IconButton
      aria-label={iconAction.label}
      color="destructive"
      variant="outline"
      size="sm"
      onClick={iconAction.onClick}
      disabled={iconAction.disabled}
    >
      {iconAction.icon}
    </IconButton>
  );

  return (
    <BaseDrawer.Root open={open} onOpenChange={onOpenChange} modal={modal} swipeDirection="down">
      <BaseDrawer.Portal>
        <StyledBackdrop />
        <StyledViewport>
          <StyledPopup $maxHeight={maxHeight}>
            <StyledContent>
              {/* Sits above the header and outside it, so a back arrow does not shift the
                  handle off centre. */}
              {!hideHandle && (
                <DragIndicator aria-hidden="true">
                  <Handle />
                </DragIndicator>
              )}

              {headerIcon && <HeaderIcon aria-hidden="true">{headerIcon}</HeaderIcon>}

              {(title || backButton || closeButton) && (
                <Header $centered={Boolean(headerIcon)}>
                  {backButton}
                  {title && (
                    <BaseDrawer.Title
                      render={
                        <Typography
                          variant="body1"
                          fontWeight={500}
                          style={{ flex: 1, minWidth: 0 }}
                        />
                      }
                    >
                      {title}
                    </BaseDrawer.Title>
                  )}
                  {closeButton}
                </Header>
              )}

              <SheetContent>{children}</SheetContent>

              {(iconAction || primaryAction || secondaryAction) && (
                <Actions>
                  {(iconAction || primaryAction) && (
                    <ActionRow>
                      {iconButton}
                      {primaryButton}
                    </ActionRow>
                  )}
                  {secondaryButton}
                </Actions>
              )}
            </StyledContent>
          </StyledPopup>
        </StyledViewport>
      </BaseDrawer.Portal>
    </BaseDrawer.Root>
  );
};

export default BottomSheet;

BottomSheet.displayName = 'BottomSheet';
